import PanelError from '@wandb/weave/common/components/elements/PanelError';
import {maybePluralizeWord} from '@wandb/weave/core/util/string';
import {isEmpty} from 'lodash';
import React, {useEffect, useRef, useState} from 'react';

import {useRampFlagMediaPanelLayoutRefactor} from '../../util/rampFeatureFlags';
import {useStablePanelId} from '../Panel/PanelIDContext';
import {InstrumentedCenteredWaveLoader as Loader} from './../../components/utility/InstrumentedLoader';
import {MediaBrowserPanelProps} from './Component';
import {useMediaSliderKey} from './hooks/useMediaSliderKey';
import {MediaBrowserPanelInner} from './MediaBrowserPanelInner';
import {MediaBrowserPanelTrackingContextProvider} from './MediaBrowserPanelTrackingContext';
import {PanelBody} from './PanelBody';
import {useMediaBrowserDimensions} from './useMediaBrowserDimensions';
import {useMediaPanelValues as useMediaPanelValuesOriginal} from './useMediaPanelValues';
import {useMediaPanelValues as useMediaPanelValuesRefactored} from './useMediaPanelValues.refactored';
import {getMediaKeysDisplayStringParts} from './utils/getMediaKeys';

const MediaBrowserPanelComp = (
  props: MediaBrowserPanelProps & {isMaxStepLoading: boolean}
) => {
  const {data, configMode, updateConfig} = props;

  // Call both hooks unconditionally (React rule: hooks can't be conditional)
  const originalValues = useMediaPanelValuesOriginal(props);
  const refactoredValues = useMediaPanelValuesRefactored(props);

  // Feature flag: Set to true to test refactored hook
  // For debugging, import {compareHookOutputs} from './useMediaPanelValues.quicktest';
  const useRefactoredLayout = useRampFlagMediaPanelLayoutRefactor();

  // Choose which result to use
  const {
    mediaType,
    layout,
    tilePagination,
    mediaKeys,
    defaultKeys,
    allMediaKeys,
    keyTypes,
    numericKeys,
    mediaString,
    flattenedMediaMetadataForAllRuns,
    firstMediaMetadata,
    currentStep,
    currentSliderValue,
    stepMeta,
    allMasks,
    allBoxes,
    configWithDefaults,
    configWithDefaultsAndMediaKeys,
    sizingSettings,
    runsWithMedia,
  } = useRefactoredLayout ? refactoredValues : originalValues;

  const panelViewerRef = useRef<HTMLDivElement | null>(null);
  const {
    width: mediaBrowserWidth,
    height: mediaBrowserHeight,
    refCallback: mediaBrowserRefCallback,
  } = useMediaBrowserDimensions();
  const mediaSliderKey = useMediaSliderKey(
    configWithDefaultsAndMediaKeys?.mediaSliderKey
  );

  // HAX: We have no way to check if this query is in progress or not after the initial render,
  // even if the media keys change. So to give the query a chance to run before we show an error,
  // we show a loading spinner for 1 second before showing the error for a better user experience.
  // State to track if we should show delayed errors
  const [showDelayedError, setShowDelayedError] = useState(false);

  // Check if we're in the "no data available" error state
  const isNoDataError =
    isEmpty(data.filtered) || flattenedMediaMetadataForAllRuns.length === 0;

  // Set up the delay timer for the "no data available" error
  useEffect(() => {
    setShowDelayedError(false);

    if (isNoDataError) {
      const timer = setTimeout(() => {
        setShowDelayedError(true);
      }, 1000);
      return () => {
        clearTimeout(timer);
      };
    }
    return undefined;
  }, [isNoDataError, mediaKeys]);

  useEffect(() => {
    // We have to set this in the global config instead of overriding the local
    // config so that the panel fetches the data
    if (mediaKeys == null) {
      updateConfig({mediaKeys: defaultKeys});
    }
  }, [mediaKeys, updateConfig, defaultKeys]);

  // The loading spinner that shows when a media panel is loading
  const renderLoader = () => (
    <div
      className="panel-media"
      style={{width: '100%', height: '100%'}}
      ref={mediaBrowserRefCallback}>
      <Loader name="media-browser-panel-loader" size="huge" />
    </div>
  );
  const stablePanelId = useStablePanelId();

  // An error that renders in the panel body so it is still editable
  const renderError = (message: React.ReactChild, logErrorMessage: string) => {
    return (
      <PanelBody
        configMode={configMode}
        panelViewerRef={panelViewerRef}
        mediaType={mediaType}
        sizingSettings={sizingSettings}
        configWithDefaults={configWithDefaults}
        allMediaKeys={allMediaKeys}
        keyTypes={keyTypes}
        numericKeys={numericKeys ?? []}
        updateConfig={updateConfig}
        mediaBrowserRefCallback={mediaBrowserRefCallback}>
        <PanelError
          panelID={stablePanelId}
          panelViewType="Media Browser"
          mediaType={mediaType ?? undefined}
          message={message}
          className="overflow-hidden"
          logErrorMessage={logErrorMessage}
        />
      </PanelBody>
    );
  };

  // only show the loader on the first data load
  if (data.loading && data.filtered.length === 0) {
    return renderLoader();
  }

  if (
    mediaKeys == null ||
    mediaKeys.length === 0 ||
    configWithDefaultsAndMediaKeys == null
  ) {
    return renderError('No Media Key selected', 'No Media Key selected');
  }

  const mediaKeysParts = getMediaKeysDisplayStringParts(mediaKeys);
  const emphasizedMediaKeys = mediaKeysParts.map((e, idx) => {
    return e.type === 'element' ? <b key={idx}>{e.value}</b> : e.value;
  });

  if (mediaString == null) {
    return renderError(
      <>
        Select runs that logged {emphasizedMediaKeys} to visualize data here.
      </>,
      `Select runs that logged ${mediaKeys.join(', ')} to visualize data here.`
    );
  }
  if (mediaString === 'data-frame') {
    return renderError(
      'Media type: "data-frame" unsupported as a media card',
      'Media type: "data-frame" unsupported as a media card'
    );
  }
  if (mediaString === 'media') {
    return renderError('Unsupported media type', 'Unsupported media type');
  }
  if (mediaType == null) {
    return renderError(
      <div>
        <p>
          Selected runs are not logging media for the key {emphasizedMediaKeys},
          but instead are logging values of type <b>{mediaString}</b>.
        </p>
        <p>
          If {emphasizedMediaKeys} is never supposed to be a media type, please
          delete this panel and create the proper panel type manually.
        </p>
      </div>,
      `Selected runs are not logging media for the key ${mediaKeys.join(', ')}, but instead are logging values of type ${mediaString}. If ${mediaKeys.join(', ')} is never supposed to be a media type, please delete this panel and create the proper panel type manually.`
    );
  }

  if (isNoDataError) {
    // Show loading spinner for the first 500ms before showing the error
    if (!showDelayedError) {
      return renderLoader();
    }

    const hasMediaSliderKey =
      mediaSliderKey != null && mediaSliderKey !== '_step';
    const mediaSliderSuffix = hasMediaSliderKey ? (
      <>
        {' '}
        at the same <b>{mediaSliderKey}</b> key
      </>
    ) : (
      ''
    );
    const msg = (
      <div>
        <p>
          No data available. Please select runs that logged the{' '}
          {maybePluralizeWord(mediaKeys.length, 'key')} {emphasizedMediaKeys}
          {mediaSliderSuffix}.
        </p>
      </div>
    );

    return renderError(
      msg,
      `No data available. Please select runs that logged the ${maybePluralizeWord(mediaKeys.length, 'key')} ${mediaKeys.join(', ')}${hasMediaSliderKey ? ` at the same ${mediaSliderKey} key` : ''}.`
    );
  }

  if (mediaBrowserWidth == null || mediaBrowserHeight == null) {
    return renderLoader();
  }

  return (
    <MediaBrowserPanelTrackingContextProvider
      mediaType={mediaType}
      mediaString={mediaString}
      panelId={(props as any).panelRef?.id}
      tileCount={layout.tiles.length}>
      <MediaBrowserPanelInner
        {...props}
        panelRef={(props as any).panelRef}
        config={configWithDefaultsAndMediaKeys}
        layout={layout}
        mediaType={mediaType}
        firstMediaMetadata={firstMediaMetadata}
        currentStep={currentStep}
        currentSliderValue={currentSliderValue}
        mediaBrowserWidth={mediaBrowserWidth}
        mediaBrowserHeight={mediaBrowserHeight}
        stepMeta={stepMeta}
        panelViewerRef={panelViewerRef}
        allMasks={allMasks}
        allBoxes={allBoxes}
        sizingSettings={sizingSettings}
        allMediaKeys={allMediaKeys}
        keyTypes={keyTypes}
        numericKeys={numericKeys ?? []}
        tilePagination={tilePagination}
        mediaBrowserRefCallback={mediaBrowserRefCallback}
        runsWithMedia={runsWithMedia}
      />
    </MediaBrowserPanelTrackingContextProvider>
  );
};

export const MediaBrowserPanel = React.memo(MediaBrowserPanelComp);

// disabling these analytics for now since it's chatty (and thus costly in segment),
// but it might be useful in the future

// type UseMediaPanelAnalyticsParams = {
//   mediaType: MediaCardType | null;
//   hasBoxes: boolean;
//   hasMasks: boolean;
//   layout: Layout;
//   tilePagination: TilePagination | null;
// };

// export function useMediaPanelAnalytics({
//   mediaType,
//   hasBoxes,
//   hasMasks,
//   layout,
//   tilePagination,
// }: UseMediaPanelAnalyticsParams): void {
//   const paginatedTiles = useMemo(
//     () => getPaginatedTiles(layout.tiles, tilePagination),
//     [layout.tiles, tilePagination]
//   );
//     const nonEmptyTileCount: number = useMemo(() => {
//     let count = 0;
//     for (const tileOrGroup of paginatedTiles) {
//       count += getNonEmptyTileCount(tileOrGroup);
//     }
//     return count;
//   }, [paginatedTiles]);
//   useEffect(() => {
//     window.analytics?.track('Media Panel Rendered', {
//       type: mediaType,
//       hasBoxes,
//       hasMasks,
//       tileCount: nonEmptyTileCount,
//     });
//   }, [mediaType, hasBoxes, hasMasks, nonEmptyTileCount]);
// }
